import { getLocalStorageItem, setLocalStorageItem } from '../utils/localStorageUtils';
/**
 * Date Time Format Configuration
 * Used for generating chart names in NewChartModal
 */

export interface DateTimeFormat {
  id: string;
  pattern: string;
  example: string;
  label: string;
}

export const DATE_TIME_FORMATS: DateTimeFormat[] = [
  { id: 'eu-short', pattern: 'DD.MM.YY HH:mm', example: '30.11.25 14:35', label: 'EU Short' },
  { id: 'iso', pattern: 'YYYY-MM-DD HH:mm', example: '2025-11-30 14:35', label: 'ISO Standard' },
  { id: 'readable', pattern: 'DD MMM YYYY HH:mm', example: '30 Nov 2025 14:35', label: 'Readable' },
  { id: 'us-ampm', pattern: 'MMM DD, YYYY h:mm A', example: 'Nov 30, 2025 2:35 PM', label: 'US AM/PM' }
];

export const DEFAULT_DATE_TIME_FORMAT_ID = 'eu-short';

export const STORAGE_KEY_DATE_TIME_FORMAT = 'user-settings-dateTimeFormat';

/**
 * Get saved date time format from localStorage
 */
export const getSavedDateTimeFormat = (): DateTimeFormat => {
  const savedId = getLocalStorageItem<string | null>(STORAGE_KEY_DATE_TIME_FORMAT, null);
  if (savedId) {
    const format = DATE_TIME_FORMATS.find(f => f.id === savedId);
    if (format) return format;
  }
  return DATE_TIME_FORMATS.find(f => f.id === DEFAULT_DATE_TIME_FORMAT_ID)!;
};

/**
 * Save date time format to localStorage
 */
export const saveDateTimeFormat = (formatId: string): void => {
  setLocalStorageItem(STORAGE_KEY_DATE_TIME_FORMAT, formatId);
};

/**
 * Format date according to pattern
 */
export const formatDateTime = (date: Date, pattern: string): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const yearShort = year.slice(-2);
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[date.getMonth()];

  // Use placeholder for MMM to prevent A in month names (Apr, Mar, Aug) from being replaced by AM/PM
  const MONTH_PLACEHOLDER = '\x00MONTH\x00';

  return pattern
    .replace('YYYY', year)
    .replace('YY', yearShort)
    .replace('MMM', MONTH_PLACEHOLDER)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', String(hours24).padStart(2, '0'))
    .replace('hh', String(hours12).padStart(2, '0'))
    .replace('h', String(hours12))
    .replace('mm', minutes)
    .replace('ss', seconds)
    .replace('A', ampm)
    .replace(MONTH_PLACEHOLDER, monthName);
};

/**
 * Generate chart name using saved format
 */
export const generateChartName = (date: Date = new Date()): string => {
  const format = getSavedDateTimeFormat();
  return formatDateTime(date, format.pattern);
};

/**
 * Date-only patterns for each format
 * More reliable than regex extraction
 */
const DATE_ONLY_PATTERNS: Record<string, string> = {
  'eu-short': 'DD.MM.YY',
  'iso': 'YYYY-MM-DD',
  'readable': 'DD MMM YYYY',
  'us-ampm': 'MMM DD, YYYY'
};

/**
 * Get date-only pattern for current format
 */
const getDateOnlyPattern = (formatId: string): string => {
  return DATE_ONLY_PATTERNS[formatId] || 'DD.MM.YY';
};

/**
 * Format date only from ISO string according to saved format
 * @param isoString - ISO 8601 string like "2026-01-03T21:31:05.123456+02:00"
 * @returns Formatted date string like "03.01.26" (EU Short) or "2026-01-03" (ISO)
 */
export const formatDateFromISO = (isoString: string): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  const format = getSavedDateTimeFormat();
  const datePattern = getDateOnlyPattern(format.id);
  return formatDateTime(date, datePattern);
};

/**
 * Parse time, microseconds and timezone from ISO string
 * @param isoString - ISO 8601 string like "2026-01-03T21:31:05.123456+02:00"
 * @returns Object with time "HH:MM:SS", microseconds "123456", and timezone "UTC+02:00"
 */
export const parseTimeWithMicroseconds = (isoString: string): { time: string; microseconds: string; timezone: string } => {
  if (!isoString) return { time: '', microseconds: '', timezone: '' };
  // ISO: "2026-01-03T21:31:05.123456+02:00"
  const match = isoString.match(/T(\d{2}:\d{2}:\d{2})\.?(\d+)?([+-]\d{2}:\d{2}|Z)?/);
  if (!match) return { time: '', microseconds: '', timezone: '' };

  // Format timezone as UTC+XX:XX or UTC-XX:XX
  let timezone = '';
  if (match[3]) {
    if (match[3] === 'Z') {
      timezone = 'UTC';
    } else {
      timezone = `UTC${match[3]}`;
    }
  }

  return {
    time: match[1],                    // "21:31:05"
    microseconds: match[2] || '000000', // "123456"
    timezone                            // "UTC+02:00"
  };
};

/**
 * Format full datetime from ISO for snapshots
 * @param isoString - ISO 8601 string
 * @returns Object with formatted date, time, microseconds and timezone
 */
export const formatDateTimeFromISO = (isoString: string): { date: string; time: string; microseconds: string; timezone: string } => {
  const date = formatDateFromISO(isoString);
  const { time, microseconds, timezone } = parseTimeWithMicroseconds(isoString);
  return { date, time, microseconds, timezone };
};
